import React, { Fragment } from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { DateTime } from "luxon";
import { addDaySuffix } from "@ecologi/shared";
import { Wrapper } from "../Wrapper";
import { getResizedImageURL } from "../utils/get-resized-image-url";
import { theme } from "../theme";

type Props = {
  isStandalone?: boolean;
  subtitleText?: string;
  title: string;
  data: any;
};

const Hero = styled.div<{ isImageless?: boolean }>`
  background: ${theme.colors.charcoal};
  color: ${theme.colors.snowdrop};
  overflow: visible;
  position: relative;
  margin-bottom: 80px;
  z-index: 1;

  @media ${theme.bp.tabletUp} {
    min-height: 500px;
    margin-bottom: 180px;

    ${(props) =>
      props.isImageless &&
      css`
        margin-bottom: 100px;
        min-height: 380px;
      `}
  }

  &::after {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1600' height='160' viewBox='0 0 1600 159.282'%3E%3Cdefs%3E%3Cstyle%3E .cls-1 %7B fill: %2328c382; fill-rule: evenodd; opacity: 0.6; %7D %3C/style%3E%3C/defs%3E%3Cpath class='cls-1' d='M763,448.206S940.776,450.1,1086,478s248.65,51.513,458,1,386.01-55.958,485-30.794,334,0,334,0V584.432S2215.56,616.314,1976,556s-296.01-20.17-487,14-297.67-33.671-437-38c-112.375-3.491-200.548,29.143-289,52.432V448.206Z' transform='translate(-763 -433.562)'/%3E%3C/svg%3E%0A");
    background-position: 0;
    background-repeat: repeat-x;
    background-size: contain;
    bottom: -80px;
    content: "";
    height: 160px;
    left: 0;
    position: absolute;
    width: 100%;
  }
`;

const Content = styled.div`
  padding: 120px 0 40px;

  @media ${theme.bp.tabletUp} {
    padding: 0;
    position: relative;
    top: 110px;
    width: 768px;
  }
`;

const Title = styled.h1`
  font-size: 38px;
  font-weight: bold;
  line-height: 1.3;
  margin-bottom: 20px;

  @media ${theme.bp.tabletUp} {
    font-size: 42px;
  }
`;

const Subtitle = styled.h2`
  font-size: 22px;
  font-weight: 300;
  line-height: 1.3;
  margin-bottom: 20px;
  max-width: 660px;
  position: relative;
  top: -10px;

  @media ${theme.bp.tabletUp} {
    font-size: 26px;
  }
`;

const AuthorMeta = styled.div`
  font-size: 16px;
  margin-bottom: 20px;
  padding: 0 0 0 54px;
  position: relative;
`;

const AuthorImage = styled.img`
  border-radius: 50%;
  display: block;
  height: 40px;
  left: 0;
  position: absolute;
  width: 40px;
`;

const AuthorName = styled.p`
  font-weight: bold;
`;

const HeroImage = styled.div`
  background-position: 50%;
  background-size: cover;
  display: block;
  height: 200px;
  left: -20px;
  position: relative;
  width: calc(100% + 40px);
  z-index: 99;

  @media ${theme.bp.mobileUp} {
    height: 400px;
  }

  @media ${theme.bp.desktopUp} {
    left: -20%;
    height: 500px;
    width: 140%;
  }
`;

const HeroImageCaption = styled.p`
  color: $white;
  font-size: 14px;
  margin: 0;
  opacity: 0.5;
  padding: 7px 0 10px;
  text-align: center;
  width: 100%;

  @media ${theme.bp.tabletUp} {
    color: $night;
  }
`;

export const ArticlesHero = ({
  title,
  subtitleText,
  isStandalone,
  data,
}: Props) => {
  const heroStyle = data.acf?.hero_style;
  const isNoHeroImage = heroStyle === "no_hero" || isStandalone;
  const heroImage = data.acf?.images[0] || { caption: "", alt: "", url: "" };
  const heroImageCaption = isNoHeroImage ? "" : heroImage.caption;
  const heroImageAlt = isNoHeroImage ? "" : heroImage.alt;
  const heroImageURL = isNoHeroImage ? "" : heroImage.url;
  const heroImageURLResized =
    getResizedImageURL(heroImageURL, 2000, 1000) || "";

  const author = data._embedded?.author[0];
  const authorName = author ? author.name : "";
  const authorImage = author ? author.avatar_urls["96"] : ""; // 96px size
  const timestamp = data.date_gmt;

  const date = DateTime.fromISO(timestamp);
  const day = date.toFormat("d");

  return (
    <Fragment>
      <Hero isImageless={isNoHeroImage}>
        <Wrapper style={{ position: "static" }}>
          <Content>
            <Title>{title}</Title>
            {subtitleText && <Subtitle>{subtitleText}</Subtitle>}
            {author && (
              <AuthorMeta>
                <AuthorImage src={authorImage} alt={authorName} />
                <AuthorName>{authorName}</AuthorName>
                <time dateTime={timestamp}>
                  {addDaySuffix(day)} {date.toFormat("MMMM y")}
                </time>
              </AuthorMeta>
            )}
            {!isNoHeroImage && (
              <div>
                <HeroImage
                  style={{ backgroundImage: `url(${heroImageURLResized})` }}
                  title={heroImageAlt}
                />
                {heroImageCaption && (
                  <HeroImageCaption>{heroImageCaption}</HeroImageCaption>
                )}
              </div>
            )}
          </Content>
        </Wrapper>
      </Hero>
    </Fragment>
  );
};
